import { create } from 'zustand';
import {
  ParticipantResult,
  ConsolidatedPDFRequest,
  getFeedback360Results,
  getConsolidatedParticipants,
  downloadConsolidatedPDF,
} from '@services/Feedback360Service';

/**
 * Feedback 360 Results Store State
 */
interface Feedback360ResultsState {
  // Data
  participants: ParticipantResult[];
  loading: boolean;
  error: string | null;
  downloadError: string | null;
  isGenerating: boolean;

  // Actions
  fetchParticipants: (assessmentId: number) => Promise<void>;
  fetchConsolidatedParticipants: (assessmentId: number) => Promise<void>;
  downloadPDF: (assessmentId: number, requestData: ConsolidatedPDFRequest) => Promise<{ success: boolean; filename?: string }>;
  clearError: () => void;
  clearDownloadError: () => void;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState = {
  participants: [],
  loading: false,
  error: null,
  downloadError: null,
  isGenerating: false,
};

/**
 * Feedback 360 Results Store
 */
export const useFeedback360ResultsStore = create<Feedback360ResultsState>((set, get) => ({
  ...initialState,

  /**
   * Fetch participants for an assessment (only those who have attempted)
   */
  fetchParticipants: async (assessmentId: number) => {
    set({ loading: true, error: null });

    try {
      const response = await getFeedback360Results(assessmentId);

      if (response.success && response.data) {
        set({
          participants: response.data,
          loading: false,
          error: null,
        });
      } else {
        set({
          participants: [],
          loading: false,
          error: response.error?.message || 'Failed to load participants',
        });
      }
    } catch (error: any) {
      console.error('Error fetching participants:', error);
      set({
        participants: [],
        loading: false,
        error: error.message || 'Failed to load participants',
      });
    }
  },

  /**
   * Fetch all users for consolidated report selection (includes users who haven't attempted)
   */
  fetchConsolidatedParticipants: async (assessmentId: number) => {
    set({ loading: true, error: null });

    try {
      const response = await getConsolidatedParticipants(assessmentId);

      if (response.success && response.data) {
        set({
          participants: response.data,
          loading: false,
          error: null,
        });
      } else {
        set({
          participants: [],
          loading: false,
          error: response.error?.message || 'Failed to load participants',
        });
      }
    } catch (error: any) {
      console.error('Error fetching consolidated participants:', error);
      set({
        participants: [],
        loading: false,
        error: error.message || 'Failed to load participants',
      });
    }
  },

  /**
   * Download consolidated PDF
   */
  downloadPDF: async (assessmentId: number, requestData: ConsolidatedPDFRequest) => {
    set({ isGenerating: true, downloadError: null });

    try {
      const response = await downloadConsolidatedPDF(assessmentId, requestData);

      if (response.success && response.data) {
        // Create download link
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Extract filename from Content-Disposition header or use default
        const filename = `180-Feedback-Consolidated-Result-${assessmentId}.pdf`;
        link.download = filename;

        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(url);

        set({ isGenerating: false, downloadError: null });

        return { success: true, filename };
      } else {
        set({
          isGenerating: false,
          downloadError: response.error?.message || 'Failed to download consolidated result',
        });

        return { success: false };
      }
    } catch (error: any) {
      console.error('Error downloading consolidated PDF:', error);
      set({
        isGenerating: false,
        downloadError: error.message || 'Failed to download consolidated result',
      });

      return { success: false };
    }
  },

  /**
   * Clear error
   */
  clearError: () => set({ error: null }),

  /**
   * Clear download error
   */
  clearDownloadError: () => set({ downloadError: null }),

  /**
   * Reset store to initial state
   */
  reset: () => set(initialState),
}));
